// ./src/utils/api/InsightReportService.ts
//
// ROADMAP #180 — the operations reports and their dashboard headlines (owner, 2026-10-17: "a dashboard widget about
// add-ons, vouchers, volunteers … a brief version on the dashboard as a widget and then a report in analytics"):
//   • add-ons — sold / handed out / still to hand out (`eventAddonSummary`, free) and the report (`eventAddonReport`);
//   • vouchers — the headline is `couponEventSummary` (CouponService.summary); the report is `couponRedemptionReport`;
//   • volunteers — on shift, checked in, no-shows, hours (`eventVolunteerShiftSummary`, free) and the report;
//   • ticket sales (#183) — the report (`eventTicketSalesReport`); the dashboard card's headline reads the ticket types.
// Every number is the server's. ⚠ The reports are on `insights` — refused on the server without it (against the
// EVENT's account); the screens show the lock instead of asking.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

export const ADDON_REPORT_BY = { ITEM: "item", POINT: "point" } as const;
export type AddonReportBy = (typeof ADDON_REPORT_BY)[keyof typeof ADDON_REPORT_BY];

export const VOUCHER_REPORT_BY = { TYPE: "type", ITEM: "item", POINT: "point" } as const;
export type VoucherReportBy = (typeof VOUCHER_REPORT_BY)[keyof typeof VOUCHER_REPORT_BY];

/** The voucher report's rest rows: marked redeemed by hand (no scan), and scans that recorded no items. */
export const VOUCHER_REST = { MARKED: "marked", NO_ITEMS: "no_items" } as const;

export const VOLUNTEER_REPORT_BY = { VOLUNTEER: "volunteer", ROLE: "role", SHIFT: "shift" } as const;
export type VolunteerReportBy = (typeof VOLUNTEER_REPORT_BY)[keyof typeof VOLUNTEER_REPORT_BY];

export const TICKET_SALES_REPORT_BY = { TYPE: "type", CHANNEL: "channel" } as const;
export type TicketSalesReportBy = (typeof TICKET_SALES_REPORT_BY)[keyof typeof TICKET_SALES_REPORT_BY];

export interface InsightTimelinePoint {
  at: string;
  key?: string | null;
  /** Handed out / redeemed (items, by item) / checked in. */
  count?: number | null;
  /** Checked out (volunteers). */
  out?: number | null;
}

export interface InsightWindow {
  startsAt: string;
  endsAt: string;
  count?: number | null;
}

// ── Add-ons ──────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface EventAddonSummary {
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  hasAddons?: boolean | null;
  sold?: number | null;
  handedOut?: number | null;
  toHandOut?: number | null;
  percent?: number | null;
  items?: Array<{ ticketTypeId?: string | null; name?: string | null; sold?: number | null; quantityTotal?: number | null }> | null;
}

export interface EventAddonReportRow {
  key?: string | null;
  label?: string | null;
  sold?: number | null;
  handedOut?: number | null;
  toHandOut?: number | null;
  percent?: number | null;
}

export interface EventAddonReport {
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  by?: AddonReportBy | null;
  bucketMinutes?: number | null;
  summary?: Omit<EventAddonReportRow, "key" | "label"> | null;
  rows?: EventAddonReportRow[] | null;
  timeline?: InsightTimelinePoint[] | null;
  busiestQuarter?: InsightWindow | null;
  busiestHour?: InsightWindow | null;
}

export interface AddonReportFilters {
  ticketTypeIds?: string[];
  pointIds?: string[];
}

// ── Ticket sales (#183) ──────────────────────────────────────────────────────────────────────────────────────────────

export interface EventTicketSalesReportRow {
  /** The ticket type's id (null = admissions with no type), or the sale channel's code (null = not recorded). */
  key?: string | null;
  /** The type's name; null by channel (the screen names a channel). */
  label?: string | null;
  sold?: number | null;
  quantityTotal?: number | null;
  percent?: number | null;
  /** The sold tickets' prices, in the event's currency — before discount codes, fees and taxes. */
  revenue?: number | null;
  refunded?: number | null;
  refundedValue?: number | null;
  averagePrice?: number | null;
}

export interface EventTicketSalesReport {
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  by?: TicketSalesReportBy | null;
  currencyCode?: string | null;
  /** 1440 — one point per DAY; `at` is the day (YYYY-MM-DD) at 00:00Z, a label rather than an instant. */
  bucketMinutes?: number | null;
  /** A recurring event read across all its dates: a type's limit is PER DATE — no "of N", no percent. */
  perDate?: boolean | null;
  summary?: Omit<EventTicketSalesReportRow, "key" | "label"> | null;
  rows?: EventTicketSalesReportRow[] | null;
  timeline?: InsightTimelinePoint[] | null;
  busiestDay?: InsightWindow | null;
}

export interface TicketSalesReportFilters {
  ticketTypeIds?: string[];
}

// ── Vouchers ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface CouponRedemptionReportRow {
  key?: string | null;
  label?: string | null;
  issued?: number | null;
  used?: number | null;
  redemptions?: number | null;
  marked?: number | null;
  quantity?: number | null;
  value?: number | null;
}

export interface CouponRedemptionReport {
  eventId?: string | null;
  by?: VoucherReportBy | null;
  bucketMinutes?: number | null;
  currencyCode?: string | null;
  summary?: Omit<CouponRedemptionReportRow, "key" | "label"> | null;
  rows?: CouponRedemptionReportRow[] | null;
  timeline?: InsightTimelinePoint[] | null;
  busiestQuarter?: InsightWindow | null;
  busiestHour?: InsightWindow | null;
}

export interface VoucherReportFilters {
  couponTypeIds?: string[];
  pointIds?: string[];
}

// ── Volunteers ───────────────────────────────────────────────────────────────────────────────────────────────────────

export interface VolunteerShiftCounts {
  volunteers?: number | null;
  shifts?: number | null;
  checkedIn?: number | null;
  onShift?: number | null;
  notInYet?: number | null;
  noShows?: number | null;
  /** Staff pay's measure of each shift checked in and out (rounding, minimum and break included). */
  workedMinutes?: number | null;
  scheduledMinutes?: number | null;
}

export interface EventVolunteerShiftSummary extends VolunteerShiftCounts {
  eventId?: string | null;
  /** The day counted (YYYY-MM-DD, the event's clock); null = every shift. */
  day?: string | null;
  hasShifts?: boolean | null;
}

export interface EventVolunteerShiftReportRow extends VolunteerShiftCounts {
  key?: string | null;
  label?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  pointIds?: string[] | null;
  servicePoints?: Array<{ id?: string | null; name?: string | null; removedAt?: string | null }> | null;
}

export interface EventVolunteerShiftReport {
  eventId?: string | null;
  by?: VolunteerReportBy | null;
  day?: string | null;
  bucketMinutes?: number | null;
  days?: string[] | null;
  /** The roles the event's shifts use — the report's role filter (comes with the report: crew may not list the account's roles). */
  roles?: Array<{ id?: string | null; name?: string | null }> | null;
  summary?: VolunteerShiftCounts | null;
  rows?: EventVolunteerShiftReportRow[] | null;
  timeline?: InsightTimelinePoint[] | null;
}

export interface VolunteerReportFilters {
  day?: string | null;
  roleIds?: string[];
  pointIds?: string[];
}

// ── Queries ──────────────────────────────────────────────────────────────────────────────────────────────────────────

const WINDOW = "startsAt endsAt count";

const ADDON_SUMMARY_QUERY = gql`
  query EventAddonSummary($params: EventAddonSummaryInput) {
    eventAddonSummary(params: $params) {
      code
      data {
        eventId
        eventTimeSlotId
        hasAddons
        sold
        handedOut
        toHandOut
        percent
        items { ticketTypeId name sold quantityTotal }
      }
      errors { name errors warnings }
    }
  }
`;

const SALES_FIELDS = "sold quantityTotal percent revenue refunded refundedValue averagePrice";

const TICKET_SALES_REPORT_QUERY = gql`
  query EventTicketSalesReport($params: EventTicketSalesReportInput) {
    eventTicketSalesReport(params: $params) {
      code
      data {
        eventId
        eventTimeSlotId
        by
        currencyCode
        bucketMinutes
        perDate
        summary { ${SALES_FIELDS} }
        rows { key label ${SALES_FIELDS} }
        timeline { at key count out }
        busiestDay { ${WINDOW} }
      }
      errors { name errors warnings }
    }
  }
`;

const ADDON_REPORT_QUERY = gql`
  query EventAddonReport($params: EventAddonReportInput) {
    eventAddonReport(params: $params) {
      code
      data {
        eventId
        eventTimeSlotId
        by
        bucketMinutes
        summary { sold handedOut toHandOut percent }
        rows { key label sold handedOut toHandOut percent }
        timeline { at key count out }
        busiestQuarter { ${WINDOW} }
        busiestHour { ${WINDOW} }
      }
      errors { name errors warnings }
    }
  }
`;

const VOUCHER_REPORT_QUERY = gql`
  query CouponRedemptionReport($params: CouponRedemptionReportInput) {
    couponRedemptionReport(params: $params) {
      code
      data {
        eventId
        by
        bucketMinutes
        currencyCode
        summary { issued used redemptions marked quantity value }
        rows { key label issued used redemptions marked quantity value }
        timeline { at key count out }
        busiestQuarter { ${WINDOW} }
        busiestHour { ${WINDOW} }
      }
      errors { name errors warnings }
    }
  }
`;

const SHIFT_COUNTS = "volunteers shifts checkedIn onShift notInYet noShows workedMinutes scheduledMinutes";

const SHIFT_SUMMARY_QUERY = gql`
  query EventVolunteerShiftSummary($params: EventVolunteerShiftSummaryInput) {
    eventVolunteerShiftSummary(params: $params) {
      code
      data { eventId day hasShifts ${SHIFT_COUNTS} }
      errors { name errors warnings }
    }
  }
`;

const SHIFT_REPORT_QUERY = gql`
  query EventVolunteerShiftReport($params: EventVolunteerShiftReportInput) {
    eventVolunteerShiftReport(params: $params) {
      code
      data {
        eventId
        by
        day
        bucketMinutes
        days
        roles { id name }
        summary { ${SHIFT_COUNTS} }
        rows { key label startsAt endsAt pointIds servicePoints { id name removedAt } ${SHIFT_COUNTS} }
        timeline { at key count out }
      }
      errors { name errors warnings }
    }
  }
`;

export class InsightReportService {
  /** The add-ons' headline (free) — on a recurring event's date when given (stock is per date). */
  static async addonSummary(eventId: string, eventTimeSlotId: string | null | undefined): Promise<OpResult> {
    return ApiService.executeQuery(ADDON_SUMMARY_QUERY, { params: { eventId, eventTimeSlotId: eventTimeSlotId ?? null } }, { fetchPolicy: "network-only" });
  }

  /** The add-ons report (insights). A filter is sent only when it holds something. */
  static async addonReport(eventId: string, eventTimeSlotId: string | null | undefined, by: AddonReportBy, filters: AddonReportFilters = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      ADDON_REPORT_QUERY,
      {
        params: {
          eventId,
          eventTimeSlotId: eventTimeSlotId ?? null,
          by,
          ...(filters.ticketTypeIds?.length ? { ticketTypeId: filters.ticketTypeIds } : {}),
          ...(filters.pointIds?.length ? { servicePointId: filters.pointIds } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /** #183 — the ticket sales report (insights). A filter is sent only when it holds something. */
  static async ticketSalesReport(eventId: string, eventTimeSlotId: string | null | undefined, by: TicketSalesReportBy, filters: TicketSalesReportFilters = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      TICKET_SALES_REPORT_QUERY,
      {
        params: {
          eventId,
          eventTimeSlotId: eventTimeSlotId ?? null,
          by,
          ...(filters.ticketTypeIds?.length ? { ticketTypeId: filters.ticketTypeIds } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /** The vouchers report (insights). */
  static async voucherReport(eventId: string, by: VoucherReportBy, filters: VoucherReportFilters = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      VOUCHER_REPORT_QUERY,
      {
        params: {
          eventId,
          by,
          ...(filters.couponTypeIds?.length ? { couponTypeId: filters.couponTypeIds } : {}),
          ...(filters.pointIds?.length ? { servicePointId: filters.pointIds } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /** The volunteers' headline (free): today's shifts when any starts today (the event's clock), else every shift. */
  static async shiftSummary(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(SHIFT_SUMMARY_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  /** The volunteers report (insights). */
  static async shiftReport(eventId: string, by: VolunteerReportBy, filters: VolunteerReportFilters = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      SHIFT_REPORT_QUERY,
      {
        params: {
          eventId,
          by,
          ...(filters.day ? { day: filters.day } : {}),
          ...(filters.roleIds?.length ? { volunteerRoleId: filters.roleIds } : {}),
          ...(filters.pointIds?.length ? { servicePointId: filters.pointIds } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }
}
